// The `/tache` command (docs/spec.md, Phase 2, Création par la commande /tache), parsed on
// the client to show a preview before sending; the server validates the structured request
// again.
//
//   /tache @guillaume vendredi Corriger l'export des biens archivés
//   Le client de Lyon attend le fichier pour lundi.   ← following lines: the description
//
// After the command, in this order and all optional: an @username (the assignee), then a due
// date: aujourd'hui, demain, a weekday (the next one, never today), JJ/MM (this year, or next
// year once passed) or JJ/MM/AAAA. The rest of the first line is the title.

export interface TaskCommand {
  /** Without the @, as typed (lower case). */
  assignee: string | null;
  /** YYYY-MM-DD, or null. */
  due: string | null;
  title: string;
  body: string;
}

export type ParseResult = { ok: true; task: TaskCommand } | { ok: false; error: string };

const COMMAND = /^\/t[aâ]che(?=\s|$)/i;
const MAX_TITLE = 200;
const WEEKDAYS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

/** True when the text starts with the command (the composer switches to task mode). */
export function isTaskCommand(text: string): boolean {
  return COMMAND.test(text.trimStart());
}

/** While typing `/`, `/t`, `/tâ`…: the command to suggest. */
export function suggestsCommand(text: string): boolean {
  const t = text.trimStart().toLowerCase();
  return t.length > 0 && t.length < 6 && ('/tache'.startsWith(t) || '/tâche'.startsWith(t));
}

const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function addDays(d: Date, n: number): Date {
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  r.setDate(r.getDate() + n);
  return r;
}

/** A due date word, or null if `word` is not one. `today` is a local date. */
export function parseDue(word: string, today: Date): string | null {
  const w = word.toLowerCase().replace(/[’`]/g, "'");
  if (w === "aujourd'hui") return iso(today);
  if (w === 'demain') return iso(addDays(today, 1));
  const day = WEEKDAYS.indexOf(w);
  if (day >= 0) {
    const ahead = (day - today.getDay() + 7) % 7 || 7;
    return iso(addDays(today, ahead));
  }
  const m = /^(\d{1,2})\/(\d{1,2})(?:\/(\d{4}))?$/.exec(w);
  if (m) {
    const [dd, mm] = [Number(m[1]), Number(m[2])];
    let year = m[3] ? Number(m[3]) : today.getFullYear();
    const valid = (y: number) => {
      const d = new Date(y, mm - 1, dd);
      return d.getMonth() === mm - 1 && d.getDate() === dd ? d : null;
    };
    let date = valid(year);
    if (!date) return null;
    if (!m[3] && iso(date) < iso(today)) {
      year += 1;
      date = valid(year);
      if (!date) return null;
    }
    return iso(date);
  }
  return null;
}

export function parseTaskCommand(text: string, today: Date = new Date()): ParseResult {
  const trimmed = text.trimStart();
  const command = COMMAND.exec(trimmed);
  if (!command) return { ok: false, error: 'Ce message ne commence pas par /tache' };
  const rest = trimmed.slice(command[0].length);
  const newline = rest.indexOf('\n');
  const first = (newline >= 0 ? rest.slice(0, newline) : rest).trim();
  const body = newline >= 0 ? rest.slice(newline + 1).trim() : '';
  const words = first.split(/\s+/).filter(Boolean);

  let assignee: string | null = null;
  if (words[0]?.startsWith('@')) {
    assignee = words.shift()!.slice(1).toLowerCase();
    if (!/^[a-z0-9._-]+$/.test(assignee)) return { ok: false, error: `Nom d'utilisateur invalide : @${assignee}` };
  }
  let due: string | null = null;
  if (words[0]) {
    due = parseDue(words[0], today);
    if (due) words.shift();
  }
  const title = words.join(' ');
  if (!title && body) {
    return { ok: false, error: "L'intitulé va sur la première ligne, juste après la commande ; les lignes suivantes forment la description" };
  }
  if (!title) return { ok: false, error: "Ajoutez l'intitulé de la tâche après la commande" };
  if (title.length > MAX_TITLE) return { ok: false, error: `L'intitulé fait au plus ${MAX_TITLE} caractères` };
  return { ok: true, task: { assignee, due, title, body } };
}

/** « vendredi 13 nov. » for a YYYY-MM-DD date. */
export function dueLabel(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'short' }).format(new Date(y!, m! - 1, d!));
}
